import React, { useMemo, useState } from "react";
import { Button, Table } from "react-bootstrap";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import type { KpiGridColumn, KpiGridFixedRow } from "../types/kpiEntryMode";

type Props = {
  columns: KpiGridColumn[];
  fixedRows: KpiGridFixedRow[];
  gridMinRows: number | "";
  gridShowColumnTotals: boolean;
  gridShowRowTotals: boolean;
};

/**
 * Read-only collapsible preview of how the GRID will look at entry time.
 */
const KpiGridPreview: React.FC<Props> = ({
  columns,
  fixedRows,
  gridMinRows,
  gridShowColumnTotals,
  gridShowRowTotals,
}) => {
  const [open, setOpen] = useState(false);

  const activeColumns = useMemo(
    () =>
      [...columns]
        .filter((c) => c.is_active !== false)
        .sort((a, b) => (a.list_order ?? 0) - (b.list_order ?? 0)),
    [columns]
  );

  const activeFixed = useMemo(
    () =>
      [...fixedRows]
        .filter((r) => r.is_active !== false)
        .sort((a, b) => (a.list_order ?? 0) - (b.list_order ?? 0)),
    [fixedRows]
  );

  const freePlaceholderCount = Math.max(
    0,
    gridMinRows === "" || gridMinRows == null ? 0 : Number(gridMinRows)
  );

  const freePlaceholders = Array.from({ length: freePlaceholderCount }, (_, i) => i);

  let tableMinWidthRem = activeColumns.length * 11;
  if (gridShowRowTotals) tableMinWidthRem += 8;
  tableMinWidthRem = Math.max(40, tableMinWidthRem);

  return (
    <div className="mt-4" data-testid="kpi-grid-preview">
      <Button
        variant="link"
        className="px-0 text-decoration-none d-inline-flex align-items-center gap-1"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="kpi-grid-preview-toggle"
      >
        {open ? <FiChevronUp /> : <FiChevronDown />}
        <span className="fw-semibold">Grid Preview</span>
      </Button>

      {open ? (
        <div className="border rounded p-3 mt-2 bg-light">
          {activeColumns.length === 0 ? (
            <div className="text-muted small mb-0">
              Add columns to preview the grid.
            </div>
          ) : (
            <div className="table-responsive">
              <Table
                bordered
                className="mb-0 align-middle bg-white kpi-field-grid"
                style={{ minWidth: `${tableMinWidthRem}rem` }}
              >
                <thead className="table-light">
                  <tr>
                    {activeColumns.map((col, i) => (
                      <th key={col.kpi_grid_column_id ?? `prev-col-${i}`}>
                        {col.label || `Column ${i + 1}`}
                      </th>
                    ))}
                    {gridShowRowTotals ? (
                      <th className="text-muted">Row total</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {freePlaceholders.map((i) => (
                    <tr key={`free-ph-${i}`}>
                      {activeColumns.map((col, ci) => (
                        <td
                          key={col.kpi_grid_column_id ?? `free-${i}-${ci}`}
                        />
                      ))}
                      {gridShowRowTotals ? (
                        <td className="text-muted table-light" />
                      ) : null}
                    </tr>
                  ))}
                  {activeFixed.map((row, ri) => (
                    <tr key={row.kpi_grid_fixed_row_id ?? `fixed-ph-${ri}`}>
                      {activeColumns.map((col, ci) => (
                        <td key={col.kpi_grid_column_id ?? `fixed-${ri}-${ci}`}>
                          {ci === 0 ? (
                            <span className="fw-semibold">{row.label || "—"}</span>
                          ) : (
                            <span className="text-muted"> </span>
                          )}
                        </td>
                      ))}
                      {gridShowRowTotals ? (
                        <td className="text-muted table-light" />
                      ) : null}
                    </tr>
                  ))}
                  {gridShowColumnTotals ? (
                    <tr className="table-light fw-semibold">
                      {activeColumns.map((col, ci) => (
                        <td key={col.kpi_grid_column_id ?? `tot-${ci}`}>
                          {ci === 0 ? "Total" : ""}
                        </td>
                      ))}
                      {gridShowRowTotals ? <td /> : null}
                    </tr>
                  ) : null}
                  {freePlaceholderCount === 0 &&
                  activeFixed.length === 0 &&
                  !gridShowColumnTotals ? (
                    <tr>
                      <td
                        colSpan={
                          activeColumns.length + (gridShowRowTotals ? 1 : 0)
                        }
                        className="text-muted small"
                      >
                        No free-row placeholders or fixed rows yet.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default KpiGridPreview;
